import type { AccessStateName, Channel, CreditTransaction } from "./types"

/* ============== Dates ============== */

// Every date is shown on the Ulaanbaatar calendar, wherever the server or the browser is.
// A fixed zone also keeps the server-rendered text and the browser's text identical.
const ZONE = 'Asia/Ulaanbaatar'

function partsOf(date: string | Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(date))
  const get = (type: string) => parts.find(part => part.type === type)?.value || ''
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') === '24' ? '00' : get('hour'), minute: get('minute') }
}

export function formatDate(date?: string | Date | null) { // 2026.10.06
  if(!date) return '-'
  const { year, month, day } = partsOf(date)
  return `${year}.${month}.${day}`
}

export function formatDateTime(date?: string | Date | null) { // 2026.10.06 14:05
  if(!date) return '-'
  const { year, month, day, hour, minute } = partsOf(date)
  return `${year}.${month}.${day} ${hour}:${minute}`
}

export function dayKey(date: string | Date) { // 2026-10-06
  const { year, month, day } = partsOf(date)
  return `${year}-${month}-${day}`
}

export function daysLeft(date?: string | Date | null, now: Date = new Date()) {
  if(!date) return 0
  return Math.max(0, Math.ceil((new Date(date).getTime() - now.getTime()) / (24 * 60 * 60 * 1000)))
}

/* ============== Numbers ============== */

export function formatCredits(amount?: number | null) { // 12000 => 12,000
  return new Intl.NumberFormat('en-US').format(amount || 0)
}

export function formatMoney(amount?: number | null) { // 12000 => 12,000₮
  return `${formatCredits(amount)}₮`
}

/* ============== SMS parts — the same rule the backend bills by ============== */

// Line breaks plus the ASCII characters of the operators' basic alphabet. [ \ ] ^ ` { | } ~ are
// missing on purpose: a message containing one is counted at the non-Latin size.
const LATIN = /^[\r\n\x20-\x5A\x5F\x61-\x7A]*$/

export function isLatin(text: string) {
  return LATIN.test(text)
}

// Mongolian Cyrillic in the Latin letters people text with (as backend/lib/sms.js). Ө and Ү become u, as in "Ulaanbaatar",
// "Unur". Anything else is left as it is.
const CYRILLIC: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'ye', ё: 'yo', ж: 'j', з: 'z', и: 'i', й: 'i', к: 'k', л: 'l', м: 'm', н: 'n',
  о: 'o', ө: 'u', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ү: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sh',
  ъ: '', ы: 'y', ь: 'i', э: 'e', ю: 'yu', я: 'ya',
  // Mongolian quotes are outside the operators' basic alphabet.
  '«': '"', '»': '"',
}

export function toLatin(text: string) {
  // A company's form reads better as people write it in Latin than letter by letter.
  const characters = Array.from(String(text || '').replace(/(^|[\s«"(])ХХК(?=$|[\s»")])/g, '$1LLC'))
  const isUpper = (character?: string) => Boolean(character) && character !== character!.toLowerCase()
  return characters.map((character, index) => {
    const lower = character.toLowerCase()
    const latin = CYRILLIC[lower]
    if(latin === undefined) return character
    if(!isUpper(character) || !latin) return latin
    // Inside a word in capitals the whole sound is in capitals: ЦЭЦЭГ is TSETSEG, Цэцэг is Tsetseg.
    return isUpper(characters[index - 1]) || isUpper(characters[index + 1]) ? latin.toUpperCase() : latin[0].toUpperCase() + latin.slice(1)
  }).join('')
}

// The backend's link pattern (lib/sms.js). The fallback route strips whatever this finds.
const LINK = /(?:https?:\/\/|www\.)[^\s]+|(?:[\w.+-]+@)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s]*)?/gi

export function findLinks(text: string) {
  return text ? text.match(LINK) || [] : []
}

export function countParts(text: string, latinSize = 155, unicodeSize = 65) {
  if(!text) return 0
  return Math.ceil(text.length / (isLatin(text) ? latinSize : unicodeSize))
}

// Mobicom delivers one part only, so a longer text goes as several SMS cut between words (as
// backend/lib/sms.js). Each piece fits one part at its own size.
export function splitSms(text: string, latinSize = 155, unicodeSize = 65) {
  if(!text.trim()) return []
  const fits = (value: string) => countParts(value, latinSize, unicodeSize) <= 1
  if(fits(text)) return [text]
  const pieces: string[] = []
  let current = ''
  const close = () => {
    if(current.trim()) pieces.push(current.trim())
    current = ''
  }
  for(const word of text.trim().match(/\S+\s*/g) || []) {
    if(fits((current + word).trimEnd())) {
      current += word
      continue
    }
    close()
    if(fits(word.trimEnd())) {
      current = word
      continue
    }
    for(const character of Array.from(word)) {
      if(!fits((current + character).trimEnd())) close()
      current += character
    }
  }
  close()
  return pieces
}

/* ============== Recipients ============== */

export function normalizePhone(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return digits.length === 11 && digits.startsWith('976') ? digits.slice(3) : digits
}

export const SMS_CARRIERS = ['mobicom', 'unitel', 'skytel', 'gmobile', 'ondo', 'lime'] as const
export type SmsCarrier = typeof SMS_CARRIERS[number]

// The operator that owns a number, by its prefix (as backend/lib/carrier.js). 50 and 55 are split
// in half: xx00 to xx49 is Unitel's, xx50 to xx99 Mobicom's.
const CARRIER_PREFIX: Record<string, SmsCarrier> = {
  80: 'unitel', 86: 'unitel', 88: 'unitel', 89: 'unitel',
  85: 'mobicom', 94: 'mobicom', 95: 'mobicom', 99: 'mobicom',
  69: 'skytel', 90: 'skytel', 91: 'skytel', 92: 'skytel', 96: 'skytel',
  78: 'gmobile', 83: 'gmobile', 87: 'gmobile', 93: 'gmobile', 97: 'gmobile', 98: 'gmobile',
  60: 'ondo', 66: 'ondo',
  72: 'lime',
}

export function detectCarrier(phone: string): SmsCarrier | 'unknown' {
  const digits = normalizePhone(phone)
  if(digits.length !== 8) return 'unknown'
  if(digits.startsWith('50') || digits.startsWith('55')) return digits[2] < '5' ? 'unitel' : 'mobicom'
  if(digits.startsWith('8181')) return 'ondo'
  return CARRIER_PREFIX[digits.slice(0, 2)] || 'unknown'
}

// One per line, or separated by commas, semicolons or spaces.
export function splitRecipients(value: string) {
  return value.split(/[\s,;]+/).map(entry => entry.trim()).filter(Boolean)
}

/* ============== Labels ============== */

type Tone = 'success' | 'danger' | 'warning' | 'blue' | 'purple' | 'muted'

const toneClasses: Record<Tone, string> = {
  success: 'bg-success/10 text-success',
  danger: 'bg-danger/10 text-danger',
  warning: 'bg-warning/10 text-warning',
  blue: 'bg-blue/10 text-blue',
  purple: 'bg-purple/10 text-purple',
  muted: 'bg-muted text-muted-foreground',
}

// A soft pill. With `dot` it reads as a state; without, as a kind.
export function SoftBadge({ tone = 'muted', dot = false, className = '', children }: { tone?: Tone, dot?: boolean, className?: string, children: React.ReactNode }) {
  return (
    <span className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${toneClasses[tone]} ${className}`}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  )
}

// What Resend reported about a sent email, and what each report means to the customer.
export const deliveryText: Record<string, { label: string, tone: Tone, note?: string }> = {
  delivered: { label: 'Хүргэгдсэн', tone: 'success' },
  delayed: { label: 'Хүргэлт саатсан', tone: 'warning', note: 'Хүлээн авагчийн имэйл сервер түр хүлээж авахгүй байгаа тул дахин оролдож байна.' },
  bounced: { label: 'Буцсан', tone: 'danger', note: 'Хүлээн авагчийн имэйл сервер энэ имэйлийг хүлээж аваагүй. Хаяг буруу эсвэл идэвхгүй байж болзошгүй.' },
  complained: { label: 'Спам гэж тэмдэглэсэн', tone: 'danger', note: 'Хүлээн авагч энэ имэйлийг спам гэж тэмдэглэсэн. Энэ хаяг руу дахин илгээхгүй байхыг зөвлөж байна.' },
  suppressed: { label: 'Хаалттай хаяг', tone: 'danger', note: 'Энэ хаяг өмнө нь буцаж эсвэл спам гэж тэмдэглэгдсэн тул имэйлийг илгээгээгүй.' },
  failed: { label: 'Хүргэгдээгүй', tone: 'danger', note: 'Имэйл үйлчилгээ энэ имэйлийг хүргэж чадсангүй.' },
  opened: { label: 'Нээсэн', tone: 'blue' },
  clicked: { label: 'Холбоос дарсан', tone: 'blue' },
}

// A sent email shows what happened to it once that is known; everything else shows its status.
export function getMessageStatusBadge(status: string, delivery?: string | null) {
  const reported = status === 'sent' && delivery ? deliveryText[delivery] : null
  if(reported) return <SoftBadge tone={reported.tone} dot>{reported.label}</SoftBadge>
  switch (status) {
    case 'sent':
      return <SoftBadge tone="success" dot>Илгээгдсэн</SoftBadge>
    case 'failed':
      return <SoftBadge tone="danger" dot>Амжилтгүй</SoftBadge>
    case 'processing':
      return <SoftBadge tone="blue" dot>Илгээж байна</SoftBadge>
    case 'queued':
      return <SoftBadge tone="warning" dot>Дараалалд</SoftBadge>
    default:
      return <SoftBadge>Тодорхойгүй</SoftBadge>
  }
}

export function getBatchStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <SoftBadge tone="success" dot>Дууссан</SoftBadge>
    case 'processing':
      return <SoftBadge tone="blue" dot>Илгээж байна</SoftBadge>
    default:
      return <SoftBadge>Тодорхойгүй</SoftBadge>
  }
}

export function getChannelBadge(channel: Channel | string) {
  if(channel === 'sms') return <SoftBadge tone="purple">SMS</SoftBadge>
  if(channel === 'email') return <SoftBadge tone="blue">Имэйл</SoftBadge>
  return <SoftBadge>-</SoftBadge>
}

// How a project's state is named and coloured everywhere it appears.
export function accessLabel(state: AccessStateName, inGrace = false): { text: string, tone: Tone } {
  switch (state) {
    case 'production':
      return inGrace ? { text: 'Төлбөр хүлээгдэж буй', tone: 'warning' } : { text: 'Идэвхтэй', tone: 'success' }
    case 'trial':
      return { text: 'Туршилт', tone: 'blue' }
    case 'lapsed':
      return { text: 'Хугацаа дууссан', tone: 'danger' }
    default:
      return { text: 'Идэвхжээгүй', tone: 'warning' }
  }
}

export function getAccessBadge(state: AccessStateName, inGrace = false) {
  const { text, tone } = accessLabel(state, inGrace)
  return <SoftBadge tone={tone} dot>{text}</SoftBadge>
}

// The fade behind a project's name in the switcher: its state at a glance.
export const accessGradient: Record<Tone, string> = {
  success: 'bg-linear-to-l from-success/15 via-success/5 to-transparent',
  blue: 'bg-linear-to-l from-blue/15 via-blue/5 to-transparent',
  warning: 'bg-linear-to-l from-warning/15 via-warning/5 to-transparent',
  danger: 'bg-linear-to-l from-danger/15 via-danger/5 to-transparent',
  purple: '',
  muted: '',
}

export const sourceText: Record<string, string> = { api: 'API', dashboard: 'Самбар' }

// The operator that owns the number. Which API delivered a message is never shown to customers.
export const carrierText: Record<string, string> = { unitel: 'Unitel', mobicom: 'Mobicom', skytel: 'Skytel', gmobile: 'G-Mobile', ondo: 'ONDO', lime: 'Lime' }

// 'transfer' is a payment WeSend staff recorded by hand, a bank transfer for instance. 'free' is
// what staff gave without payment: such an invoice is settled with a total of 0.
export const paymentMethodText: Record<string, string> = { qpay: 'QPay', storepay: 'Storepay', pocket: 'Pocket', transfer: 'Дансаар', free: 'Үнэ төлбөргүй' }

export function getInvoiceStatusBadge(status: string) {
  if(status === 'paid') return <SoftBadge tone="success" dot>Төлөгдсөн</SoftBadge>
  if(status === 'pending') return <SoftBadge tone="warning" dot>Хүлээгдэж буй</SoftBadge>
  return <SoftBadge tone="muted" dot>Цуцлагдсан</SoftBadge>
}

// What an invoice bought, in a few words.
export function invoiceContent(invoice: { months: number, credits: number, domainAddon?: boolean }) {
  return [
    invoice.months > 0 ? `Суурь хураамж ${invoice.months} сар` : '',
    invoice.domainAddon ? 'Өөрийн домэйн' : '',
    invoice.credits > 0 ? `${formatCredits(invoice.credits)} кредит` : '',
  ].filter(Boolean).join(' · ')
}

// How long the own-domain add-on is paid for, in words: "10 сар", "15 хоног", "9 сар 15 хоног".
export function addonTermText({ months, days }: { months: number, days: number }) {
  return [months > 0 ? `${months} сар` : '', days > 0 ? `${days} хоног` : ''].filter(Boolean).join(' ') || '0 хоног'
}

export const lotSourceText: Record<string, string> = { trial: 'Туршилтын кредит', welcome: 'Бэлэг кредит', purchase: 'Худалдан авалт', adjustment: 'WeSend-ээс олгосон' }

export const transactionTypeText: Record<CreditTransaction['type'], string> = {
  grant: 'Олгосон', purchase: 'Цэнэглэлт', charge: 'Зарцуулалт', refund: 'Буцаалт', expire: 'Хугацаа дууссан', adjustment: 'Олгосон',
}

// Charges and expiries take credits away; everything else adds them.
export function transactionSign(type: CreditTransaction['type']) {
  return type === 'charge' || type === 'expire' ? -1 : 1
}

export function fullName(person: { fullName?: string | null }) {
  return person.fullName || 'Хэрэглэгч'
}

export function initials(name?: string | null) {
  const words = (name || '').trim().split(/\s+/).filter(Boolean)
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '?').slice(0, 2)).toUpperCase()
}
